import React from "react";
import { Link } from "react-router-dom";
import { FiArrowRight, FiChevronDown } from "react-icons/fi";
import { FaWhatsapp } from "react-icons/fa";
import FormHeroSection from "./FormHeroSection";

const MicrosoftLogo = () => (
  <svg width="20" height="20" viewBox="0 0 21 21" fill="none" className="rounded-sm">
    <rect x="0.5" y="0.5" width="9" height="9" fill="#f25022" />
    <rect x="11.5" y="0.5" width="9" height="9" fill="#7fba00" />
    <rect x="0.5" y="11.5" width="9" height="9" fill="#00a4ef" />
    <rect x="11.5" y="11.5" width="9" height="9" fill="#ffb900" />
  </svg>
);

const DiamondIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="text-white/80">
    <path d="M12 2l9 10-9 10-9-10L12 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" fill="rgba(255,255,255,0.12)" />
  </svg>
);

const Main = () => {
  return (
    <main className="relative flex min-h-screen w-full flex-col items-center justify-center overflow-hidden px-4 pb-6 pt-24 text-white sm:px-6 sm:pt-32 lg:px-8">
      {/* Background image with overlay */}
      <div
        className="absolute inset-0 -z-20 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/Images/hero/abscod-team-hero.webp')" }}
      />
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-[#050811]/95 via-[#0b1220]/85 to-[#050811]/95" />

      {/* Glow behind text */}
      <div
        className="pointer-events-none absolute inset-0 -z-[15] [background:radial-gradient(circle_at_35%_40%,rgba(255,219,22,0.12),transparent_45%),radial-gradient(circle_at_55%_35%,rgba(255,219,22,0.06),transparent_40%)]"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-12 xl:gap-16">
        {/* Left column — text content */}
      <div className="text-center lg:text-left">
        {/* Top pill */}
        <div className="mb-3 inline-flex max-w-[92%] flex-wrap items-center justify-center gap-x-2 gap-y-1 rounded-full border border-white/10 bg-white/[0.05] px-4 py-1.5 shadow-sm backdrop-blur-md sm:max-w-none">
          <span className="text-[10px] font-semibold tracking-wide text-white/85 uppercase sm:text-xs">
            Microsoft Solutions Partner
          </span>
          <span className="hidden h-3 w-px bg-white/20 sm:inline-block" />
          <span className="text-[10px] font-medium text-white/60 sm:text-xs">
            Greater Noida, India
          </span>
        </div>

        {/* Title */}
        <h1 className="mx-auto mb-3 flex max-w-3xl flex-col items-center text-center font-heading text-[1.85rem] font-extrabold leading-[1.08] tracking-[-0.04em] text-white sm:text-[2.5rem] md:text-[3rem] lg:mx-0 lg:items-start lg:text-left lg:text-[2.75rem] xl:text-[3.25rem]">
          <span className="block text-white [text-shadow:0_0_40px_rgba(255,255,255,0.12)]">
            Driving Business Growth with
          </span>
          <span className="relative mt-2 block w-fit text-[#FFDB16] drop-shadow-[0_0_24px_rgba(255,219,22,0.45)]">
            Microsoft Dynamics 365
            <span className="absolute -bottom-2 left-1/2 h-1 w-28 -translate-x-1/2 rounded-full bg-[#FFDB16] shadow-[0_0_20px_rgba(255,219,22,0.5)] sm:w-32" />
          </span>
        </h1>

        {/* Subtitle */}
        <p className="mx-auto mb-5 max-w-xl font-openSans text-base leading-7 text-white/75 sm:text-lg lg:mx-0">
          Transform your business with Microsoft Dynamics 365 Business Central—from implementation and customization to seamless integration and dedicated support. Abscod Informatics helps organizations across Noida and Delhi NCR build smarter, more efficient operations.
        </p>

        {/* CTAs */}
        <div className="flex flex-col items-center justify-center gap-4 sm:flex-row lg:justify-start">
          <Link
            to="/contactus"
            className="group inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full bg-[#FFDB16] px-8 py-3.5 text-sm font-bold text-slate-950 shadow-[0_12px_40px_rgba(255,219,22,0.25)] transition duration-300 hover:-translate-y-0.5 hover:bg-white hover:shadow-[0_16px_50px_rgba(255,219,22,0.35)]"
          >
            Book a Discovery Call
            <FiArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
          </Link>

          <button
            type="button"
            className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-8 py-3.5 text-sm font-bold text-white backdrop-blur-xl transition duration-300 hover:border-white/30 hover:bg-white/[0.12]"
          >
            See How We Work
            <FiChevronDown className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {/* Trust badges */}
        <div className="mt-5 flex flex-col items-center justify-center gap-3 sm:flex-row lg:justify-start">
          <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.05] px-4 py-2.5 backdrop-blur-md transition duration-300 hover:bg-white/[0.1]">
            <MicrosoftLogo />
            <div className="text-left">
              <strong className="block text-sm font-semibold text-white">
                Microsoft Solutions Partner
              </strong>
              <small className="block text-xs text-white/60">
                Business Applications
              </small>
            </div>
          </div>

          <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.05] px-4 py-2.5 backdrop-blur-md transition duration-300 hover:bg-white/[0.1]">
            <DiamondIcon />
            <div className="text-left">
              <strong className="block text-sm font-semibold text-white">
                LS Retail Diamond Partner
              </strong>
              <small className="block text-xs text-white/60">
                5 consecutive years · 2022-2026
              </small>
            </div>
          </div>
        </div>

      </div>

      {/* Right column — form */}
      <div className="mx-auto w-full max-w-md lg:mx-0 lg:max-w-none">
        <FormHeroSection />
      </div>
      </div>

      {/* WhatsApp floating button */}
      <a
        href="https://wa.me/"
        target="_blank"
        rel="noreferrer"
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-[#25d366] text-white shadow-lg transition hover:scale-105"
        aria-label="Chat on WhatsApp"
      >
        <FaWhatsapp className="h-7 w-7" />
      </a>
    </main>
  );
};

export default Main;
